import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon";
import { useICCCColors } from "./theme";
import { BOTTOM_ROW_HEIGHT } from "../model/constants";

const fmtDuration = (ms) => {
  if (!ms || ms < 0) return "—";
  const s = Math.floor(ms / 1000);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  if (days >= 365) {
    const y = Math.floor(days / 365);
    const m = Math.floor((days % 365) / 30);
    const d = (days % 365) % 30;
    return `${y}y ${m}m ${d}d`;
  }
  return `${days}d ${hours}h ${mins}m`;
};

const derivePeriods = (availability, activeAlerts) => {
  const oldest = activeAlerts.reduce((min, a) => {
    const t = a.raisedAt || 0;
    if (!t) return min;
    return min === 0 ? t : Math.min(min, t);
  }, 0);
  const unhealthyMs = oldest ? Date.now() - oldest : 0;
  const denom = Math.max(1, 100 - availability);
  const healthyMs = Math.round(unhealthyMs * (availability / denom));
  return { healthyMs, unhealthyMs };
};

const Card = ({ title, sub, value, hint, color, bg, icon, colors }) => (
  <MDBox
    sx={{
      bgcolor: bg,
      borderRadius: 1,
      p: 1.5,
      mb: 1.25,
      border: `1px solid ${color}40`,
      display: "flex",
      gap: 1.5,
      alignItems: "center",
    }}
  >
    <MDBox
      sx={{
        width: 44,
        height: 44,
        borderRadius: 1,
        bgcolor: colors.cardBg2,
        color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon>{icon}</Icon>
    </MDBox>
    <MDBox sx={{ flex: 1, minWidth: 0 }}>
      <MDTypography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600, fontSize: 11 }}>
        {title}{" "}
        {sub && (
          <MDTypography component="span" variant="caption" sx={{ color: colors.textMuted }}>
            ({sub})
          </MDTypography>
        )}
      </MDTypography>
      <MDTypography variant="h5" fontWeight="bold" sx={{ color: colors.textPrimary, lineHeight: 1.1 }}>
        {value}
      </MDTypography>
      {hint && (
        <MDTypography variant="caption" sx={{ color: colors.textMuted, fontSize: 10 }}>
          {hint}
        </MDTypography>
      )}
    </MDBox>
  </MDBox>
);

Card.propTypes = {
  title: PropTypes.string,
  sub: PropTypes.string,
  value: PropTypes.node,
  hint: PropTypes.string,
  color: PropTypes.string,
  bg: PropTypes.string,
  icon: PropTypes.string,
  colors: PropTypes.object,
};

export default function PerformanceIndicators({ rollup, activeAlerts = [], targetPct = 26 }) {
  const ICCC_COLORS = useICCCColors();
  const total = rollup?.total || 0;
  const availability = total ? ((rollup?.healthy || 0) / total) * 100 : 0;
  const { healthyMs, unhealthyMs } = derivePeriods(availability, activeAlerts);

  const avlColor =
    availability >= 80 ? ICCC_COLORS.healthy : availability >= 50 ? ICCC_COLORS.warning : ICCC_COLORS.critical;
  const meetsTarget = availability >= targetPct;

  return (
    <MDBox
      sx={{
        bgcolor: ICCC_COLORS.cardBg,
        border: `1px solid ${ICCC_COLORS.cardBorder}`,
        borderRadius: 1.5,
        p: 2,
        height: BOTTOM_ROW_HEIGHT,
        display: "flex",
        flexDirection: "column",
        boxShadow: ICCC_COLORS.shadow,
      }}
    >
      <MDTypography variant="h6" fontWeight="bold" sx={{ color: ICCC_COLORS.textPrimary, mb: 1 }}>
        Performance Indicators
      </MDTypography>
      <MDBox sx={{ flex: 1, overflow: "auto" }}>
        <Card
          title="Healthy Period"
          sub={`${availability.toFixed(1)}%`}
          value={fmtDuration(healthyMs)}
          color={ICCC_COLORS.healthy}
          bg={ICCC_COLORS.healthyBg}
          icon="event_available"
          colors={ICCC_COLORS}
        />
        <Card
          title="Unhealthy Period"
          sub={`${(100 - availability).toFixed(1)}%`}
          value={fmtDuration(unhealthyMs)}
          color={ICCC_COLORS.critical}
          bg={ICCC_COLORS.criticalBg}
          icon="event_busy"
          colors={ICCC_COLORS}
        />
        <Card
          title="Performance (Availability)"
          value={`${availability.toFixed(1)}%`}
          hint={`Target ${targetPct}% — ${meetsTarget ? "met" : "below target"}`}
          color={avlColor}
          bg={ICCC_COLORS.warningBg}
          icon="speed"
          colors={ICCC_COLORS}
        />
      </MDBox>
      <MDTypography
        variant="caption"
        sx={{ color: ICCC_COLORS.textDim, fontSize: 10, mt: 1, display: "block", flexShrink: 0 }}
      >
        Availability is live from device health; period lengths are derived from the current alert window.
      </MDTypography>
    </MDBox>
  );
}

PerformanceIndicators.propTypes = {
  rollup: PropTypes.shape({
    total: PropTypes.number,
    healthy: PropTypes.number,
  }),
  activeAlerts: PropTypes.arrayOf(PropTypes.object),
  targetPct: PropTypes.number,
};
